import React, { useState, useEffect } from 'react';
import { get, post } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import GradeAssessments from '../components/GradeAssessments';
import GradePlacements from '../components/GradePlacements';

const STATUS_COLORS = {
  approved:   { bg:'#EDF4FF', color:'#1E4FAD' },
  in_progress:{ bg:'#FFF3DC', color:'#8B5E0A' },
  ready:      { bg:'#E8F5EB', color:'#2E6B3A' },
  completed:  { bg:'#E8F5EB', color:'#155724' },
  dispatched: { bg:'#F2EFE7', color:'#556B2F' },
  delivered:  { bg:'#F2EFE7', color:'#556B2F' },
};

const PRIORITY_COLORS = {
  high:   { bg:'#FDEAEA', color:'#8F2E24' },
  medium: { bg:'#FFF3DC', color:'#8B5E0A' },
  low:    { bg:'#E8F5EB', color:'#2E6B3A' },
};

export default function ProductionDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingTask, setUpdatingTask] = useState(null);

  const isInventoryOfficer = user?.role === 'inventory_officer';

  const load = () => {
    setLoading(true);
    get('/production-dashboard')
      .then(d => setData(d || {}))
      .catch(() => setData({}))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateTaskStatus = async (taskId, status) => {
    setUpdatingTask(taskId);
    try {
      await post('/tasks/'+taskId, { status });
      load();
    } catch (err) { alert(err.message); }
    finally { setUpdatingTask(null); }
  };

  if (loading) return <div className="spinner" style={{marginTop:40}}></div>;
  if (!data) return (
    <div className="empty-state">
      <h3>Unable to load dashboard</h3>
      <p>There was an error loading your dashboard data. Please try refreshing.</p>
      <button className="btn btn-primary" onClick={load}>↻ Retry</button>
    </div>
  );

  // Inventory Officer sees a different dashboard
  if (user?.role === 'inventory_officer') {
    const low_stock = data?.low_stock || [];
    const all_materials = data?.all_materials || [];
    const stock_movement = data?.stock_movement || {};
    const pending_pos = data?.pending_pos || [];
    const my_tasks = data?.my_tasks || [];
    const recent_materials = data?.recent_materials || [];
    const inv_control = data?.inventory_control || {};
    const sku_summary = data?.sku_type_summary || [];
    const recent_mcrs = data?.recent_mcrs || [];
    return (
      <div>
        <div className="flex-between mb-lg">
          <div>
            <h2 className="page-title">Inventory Dashboard</h2>
            <p className="text-muted text-sm" style={{marginTop:4}}>
              {new Date().toLocaleDateString('en-NG',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
            </p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
        </div>

        <GradeAssessments />
        {/* Inventory summary tiles */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:20}}>
          {[
            { label:'Total Materials', value:all_materials?.length||0, color:'#556B2F', bg:'#F2EFE7' },
            { label:'Low Stock', value:low_stock?.length||0, color:low_stock?.length>0?'#8F2E24':'#2E6B3A', bg:low_stock?.length>0?'#FDEAEA':'#E8F5EB' },
            { label:'Stock In (Month)', value:Number(stock_movement?.total_in||0).toLocaleString(), color:'#2E6B3A', bg:'#E8F5EB' },
            { label:'Stock Out (Month)', value:Number(stock_movement?.total_out||0).toLocaleString(), color:'#8F2E24', bg:'#FDEAEA' },
            { label:'Pending POs', value:Array.isArray(pending_pos)?pending_pos.length:(pending_pos||0), color:'#8B5E0A', bg:'#FFF3DC' },
            { label:'My Tasks', value:my_tasks?.length||0, color:'#1E4FAD', bg:'#EDF4FF' },
          { label:'Pending Receipts', value:parseInt(inv_control.pending_receipts||0), color:parseInt(inv_control.pending_receipts||0)>0?'#8B5E0A':'#2E6B3A', bg:parseInt(inv_control.pending_receipts||0)>0?'#FFF3DC':'#E8F5EB' },
          { label:'Pending MCR', value:parseInt(inv_control.pending_mcr||0), color:parseInt(inv_control.pending_mcr||0)>0?'#8B5E0A':'#2E6B3A', bg:parseInt(inv_control.pending_mcr||0)>0?'#FFF3DC':'#E8F5EB' },
          { label:'WIP Overdue', value:parseInt(inv_control.wip_overdue||0), color:parseInt(inv_control.wip_overdue||0)>0?'#8F2E24':'#2E6B3A', bg:parseInt(inv_control.wip_overdue||0)>0?'#FDEAEA':'#E8F5EB' },
          { label:'Pending Adjustments', value:parseInt(inv_control.pending_adjustments||0), color:parseInt(inv_control.pending_adjustments||0)>0?'#8B5E0A':'#2E6B3A', bg:parseInt(inv_control.pending_adjustments||0)>0?'#FFF3DC':'#E8F5EB' },
          ].map(tile => (
            <div key={tile.label} style={{background:tile.bg,border:'1px solid '+tile.color+'33',borderRadius:10,padding:'12px 14px'}}>
              <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:tile.color,marginBottom:6}}>{tile.label}</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',fontWeight:700,color:tile.color}}>{tile.value}</div>
            </div>
          ))}
        </div>

        {/* Low stock alert */}
        {low_stock?.length > 0 && (
          <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:'10px 16px',marginBottom:16,fontSize:12,color:'#8F2E24'}}>
            📦 <strong>{low_stock.length}</strong> material{low_stock.length!==1?'s':''} below reorder level — immediate action required
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
          {/* Low stock list */}
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
            <div style={{background:'#8F2E24',padding:'12px 16px'}}>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
                📦 Low Stock Items ({low_stock?.length||0})
              </span>
            </div>
            <div style={{maxHeight:300,overflowY:'auto'}}>
              {!low_stock?.length ? (
                <div style={{padding:20,textAlign:'center',color:'#8A8A8A',fontSize:12}}>All stock levels OK</div>
              ) : low_stock.map((m,i) => (
                <div key={i} style={{padding:'10px 14px',borderBottom:'1px solid #F2EFE7',display:'flex',justifyContent:'space-between'}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:'#3B2A21'}}>{m.name}</div>
                    <div style={{fontSize:11,color:'#8A8A8A'}}>Reorder at: {m.reorder_level} {m.unit_of_measure}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:13,fontWeight:700,color:'#8F2E24'}}>{Number(m.current_stock).toLocaleString()}</div>
                    <div style={{fontSize:10,color:'#8A8A8A'}}>{m.unit_of_measure}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent materials */}
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
            <div style={{background:'#3B2A21',padding:'12px 16px'}}>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
                📋 Recent Material Activity
              </span>
            </div>
            <div style={{maxHeight:300,overflowY:'auto'}}>
              {!recent_materials?.length ? (
                <div style={{padding:20,textAlign:'center',color:'#8A8A8A',fontSize:12}}>No recent activity</div>
              ) : recent_materials.map((m,i) => (
                <div key={i} style={{padding:'10px 14px',borderBottom:'1px solid #F2EFE7',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:500,color:'#3B2A21'}}>{m.material}</div>
                    <div style={{fontSize:10,color:'#8A8A8A'}}>{m.movement_type} • {new Date(m.created_at).toLocaleDateString('en-NG')}</div>
                  </div>
                  <div style={{fontSize:12,fontWeight:600,color:m.movement_type==='in'?'#2E6B3A':'#8F2E24'}}>
                    {m.movement_type==='in'?'+':'-'}{Number(m.quantity).toLocaleString()} {m.unit}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SKU Type Summary */}
        {sku_summary.length > 0 && (
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',marginBottom:16}}>
            <div style={{background:'#556B2F',padding:'12px 16px'}}>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
                📦 SKU Stock Summary
              </span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:0}}>
              {sku_summary.map((s,i) => (
                <div key={i} style={{padding:'14px 18px',borderRight:'1px solid #F2EFE7',borderBottom:'1px solid #F2EFE7'}}>
                  <div style={{fontSize:10,color:'#8A8A8A',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>
                    {s.inventory_type}
                  </div>
                  <div style={{fontSize:'1.4rem',fontFamily:'Cormorant Garamond,serif',fontWeight:700,color:'#3B2A21'}}>
                    {s.sku_count} SKUs
                  </div>
                  {parseInt(s.low_count) > 0 && (
                    <div style={{fontSize:11,color:'#8F2E24',fontWeight:600,marginTop:2}}>
                      ⚠ {s.low_count} low stock
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent MCRs */}
        {recent_mcrs.length > 0 && (
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',marginBottom:16}}>
            <div style={{background:'#3B2A21',padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
                🔄 Recent Conversion Requests
              </span>
            </div>
            <div>
              {recent_mcrs.map((m,i) => (
                <div key={i} style={{padding:'10px 16px',borderBottom:'1px solid #F2EFE7',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>{m.mcr_number}</div>
                    <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                      {m.source_desc} → {m.dest_desc} · {m.raw_quantity_to_issue} {m.source_unit}
                    </div>
                  </div>
                  <span style={{fontSize:9,fontWeight:700,padding:'3px 10px',borderRadius:20,textTransform:'uppercase',
                    background:m.status==='completed'?'#E8F5EB':m.status==='submitted'?'#FFF3DC':m.status==='returned_for_verification'?'#FDEAEA':'#F2EFE7',
                    color:m.status==='completed'?'#2E6B3A':m.status==='submitted'?'#8B5E0A':m.status==='returned_for_verification'?'#8F2E24':'#556B2F'}}>
                    {m.status.replace(/_/g,' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* My tasks */}
        {my_tasks?.length > 0 && (
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
            <div style={{background:'#556B2F',padding:'12px 16px'}}>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
                ✅ My Tasks ({my_tasks.length})
              </span>
            </div>
            <div>
              {my_tasks.map((t,i) => (
                <div key={i} style={{padding:'10px 14px',borderBottom:'1px solid #F2EFE7',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div style={{fontSize:12,color:'#3B2A21',fontWeight:500}}>{t.title}</div>
                  <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'uppercase',
                    background:t.status==='completed'?'#E8F5EB':t.status==='in_progress'?'#EDF4FF':'#F2EFE7',
                    color:t.status==='completed'?'#2E6B3A':t.status==='in_progress'?'#1E4FAD':'#556B2F'}}>
                    {t.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const { active_jobs, jobs_due_today, jobs_due_week, completed_today,
    completed_week, my_tasks, low_stock, recent_materials, production_stats,
    all_materials, stock_movement, pending_pos,
    pending_mcrs_for_production } = data;

  const isOverdue = (date) => date && new Date(date) < new Date();

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Production Dashboard</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            {new Date().toLocaleDateString('en-NG',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
          </p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      <GradeAssessments />
      {user?.role === 'production_team_lead' && <GradePlacements />}
      {/* Summary tiles */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:20}}>
        {[
          { label:'In Progress', value:production_stats.in_progress, color:'#8B5E0A', bg:'#FFF3DC' },
          { label:'Approved', value:production_stats.approved, color:'#1E4FAD', bg:'#EDF4FF' },
          { label:'Ready', value:production_stats.ready, color:'#2E6B3A', bg:'#E8F5EB' },
          { label:'Due Today', value:jobs_due_today.length, color:jobs_due_today.length>0?'#8F2E24':'#2E6B3A', bg:jobs_due_today.length>0?'#FDEAEA':'#E8F5EB' },
          { label:'Overdue', value:production_stats.overdue, color:production_stats.overdue>0?'#8F2E24':'#2E6B3A', bg:production_stats.overdue>0?'#FDEAEA':'#E8F5EB' },
          { label:'Done This Month', value:production_stats.completed_this_month, color:'#556B2F', bg:'#F2EFE7' },
        ].map(tile => (
          <div key={tile.label} style={{background:tile.bg,border:'1px solid '+tile.color+'33',
            borderRadius:10,padding:'12px 14px'}}>
            <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',
              color:tile.color,marginBottom:6}}>{tile.label}</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.6rem',
              fontWeight:700,color:tile.color}}>{tile.value}</div>
          </div>
        ))}
      </div>

      {/* MCR Action Required Alert */}
      {pending_mcrs_for_production?.filter(m=>m.status==='submitted').length > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F0D9A0',borderRadius:8,
          padding:'12px 16px',marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:13,color:'#8B5E0A',marginBottom:8}}>
            🔄 {pending_mcrs_for_production.filter(m=>m.status==='submitted').length} Conversion Request{pending_mcrs_for_production.filter(m=>m.status==='submitted').length!==1?'s':''} Awaiting Your Acceptance
          </div>
          {pending_mcrs_for_production.filter(m=>m.status==='submitted').map((m,i)=>(
            <div key={i} style={{display:'flex',justifyContent:'space-between',
              alignItems:'center',padding:'8px 12px',background:'#fff',
              borderRadius:6,marginBottom:6,border:'1px solid #F0D9A0'}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:'#3B2A21'}}>{m.mcr_number}</div>
                <div style={{fontSize:11,color:'#8A8A8A',marginTop:2}}>
                  {m.source_desc} → {m.dest_desc} · {Number(m.raw_quantity_to_issue).toLocaleString()} {m.source_unit}
                </div>
                {m.required_completion_date && (
                  <div style={{fontSize:10,color:'#8F2E24',marginTop:2}}>
                    Required by: {new Date(m.required_completion_date).toLocaleDateString('en-NG')}
                  </div>
                )}
              </div>
              <a href="/material-conversion" style={{fontSize:11,color:'#8B5E0A',
                fontWeight:700,textDecoration:'none',padding:'4px 10px',
                border:'1px solid #8B5E0A',borderRadius:6}}>
                Review →
              </a>
            </div>
          ))}
        </div>
      )}

      {/* In-conversion reminder */}
      {pending_mcrs_for_production?.filter(m=>m.status==='accepted'||m.status==='in_conversion').length > 0 && (
        <div style={{background:'#EDF4FF',border:'1px solid #B0C8F0',borderRadius:8,
          padding:'10px 16px',marginBottom:16,fontSize:12,color:'#1E4FAD'}}>
          🔧 <strong>{pending_mcrs_for_production.filter(m=>m.status==='accepted'||m.status==='in_conversion').length}</strong> conversion{pending_mcrs_for_production.filter(m=>m.status==='accepted'||m.status==='in_conversion').length!==1?'s':''} currently in progress —
          <a href="/material-conversion" style={{color:'#1E4FAD',marginLeft:4,fontWeight:700}}>
            Record completion →
          </a>
        </div>
      )}

      {/* Alerts */}
      {(jobs_due_today.length > 0 || parseInt(production_stats.overdue) > 0 || low_stock.length > 0) && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(250px,1fr))',gap:10,marginBottom:16}}>
          {jobs_due_today.length > 0 && (
            <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:'10px 16px',fontSize:12,color:'#8B5E0A'}}>
              ⏰ <strong>{jobs_due_today.length}</strong> job order{jobs_due_today.length!==1?'s':''} due today
            </div>
          )}
          {parseInt(production_stats.overdue) > 0 && (
            <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:'10px 16px',fontSize:12,color:'#8F2E24'}}>
              🔴 <strong>{production_stats.overdue}</strong> overdue job order{production_stats.overdue!=='1'?'s':''}
            </div>
          )}
          {isInventoryOfficer && low_stock.length > 0 && (
            <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',borderRadius:8,padding:'10px 16px',fontSize:12,color:'#8F2E24'}}>
              📦 <strong>{low_stock.length}</strong> material{low_stock.length!==1?'s':''} below reorder level
            </div>
          )}
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
        {/* Active Job Orders */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <div style={{background:'#3B2A21',padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
              🏭 Active Job Orders ({active_jobs.length})
            </span>
          </div>
          <div style={{maxHeight:350,overflowY:'auto'}}>
            {active_jobs.length === 0 ? (
              <div style={{padding:20,textAlign:'center',color:'#8A8A8A',fontSize:12}}>No active job orders</div>
            ) : active_jobs.map(job => {
              const sc = STATUS_COLORS[job.status] || STATUS_COLORS.approved;
              const overdue = isOverdue(job.delivery_date) && !['completed','dispatched','delivered'].includes(job.status);
              return (
                <div key={job.id} style={{padding:'12px 16px',borderBottom:'1px solid #F2EFE7',
                  background:overdue?'#FFF8F8':'#fff'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#3B2A21'}}>{job.job_number}</div>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                      textTransform:'uppercase',background:sc.bg,color:sc.color}}>
                      {job.status.replace('_',' ')}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:'#555',marginBottom:2}}>{job.customer_name}</div>
                  <div style={{fontSize:11,color:'#8A8A8A'}}>{job.product_description}</div>
                  <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontSize:11}}>
                    <span style={{color:'#556B2F',fontWeight:500}}>Qty: {job.quantity}</span>
                    {job.delivery_date && (
                      <span style={{color:overdue?'#8F2E24':'#8A8A8A',fontWeight:overdue?700:400}}>
                        {overdue?'⚠ OVERDUE: ':''}{job.delivery_date}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* My Tasks */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <div style={{background:'#556B2F',padding:'12px 16px'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
              ✓ My Tasks ({my_tasks.length})
            </span>
          </div>
          <div style={{maxHeight:350,overflowY:'auto'}}>
            {my_tasks.length === 0 ? (
              <div style={{padding:20,textAlign:'center',color:'#8A8A8A',fontSize:12}}>No pending tasks 🎉</div>
            ) : my_tasks.map(task => {
              const pc = PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium;
              const taskOverdue = task.due_date && new Date(task.due_date) < new Date();
              return (
                <div key={task.id} style={{padding:'12px 16px',borderBottom:'1px solid #F2EFE7',
                  background:taskOverdue?'#FFF8F8':'#fff'}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:600,color:'#3B2A21',flex:1,marginRight:8}}>{task.title}</div>
                    <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                      textTransform:'uppercase',background:pc.bg,color:pc.color,flexShrink:0}}>
                      {task.priority}
                    </span>
                  </div>
                  {task.description && <div style={{fontSize:11,color:'#8A8A8A',marginBottom:4}}>{task.description}</div>}
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:6}}>
                    <span style={{fontSize:10,color:taskOverdue?'#8F2E24':'#8A8A8A',fontWeight:taskOverdue?600:400}}>
                      {taskOverdue?'⚠ Overdue: ':''}{task.due_date||'No deadline'}
                    </span>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:'2px 8px',
                      background:'#F2EFE7',color:'#556B2F'}}
                      onClick={()=>updateTaskStatus(task.id,'in_progress')}
                      disabled={updatingTask===task.id||task.status==='in_progress'}>
                      {task.status==='in_progress'?'In Progress':'Start'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Due This Week */}
      {jobs_due_week.length > 0 && (
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',marginBottom:16}}>
          <div style={{background:'#8B5E0A',padding:'12px 16px'}}>
            <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
              📅 Due This Week ({jobs_due_week.length})
            </span>
          </div>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead><tr style={{background:'#F2EFE7'}}>
              {['Job No.','Customer','Product','Qty','Due Date','Status'].map(h=>(
                <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {jobs_due_week.map((job,i) => {
                const sc = STATUS_COLORS[job.status] || STATUS_COLORS.approved;
                return (
                  <tr key={job.id} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#556B2F'}}>{job.job_number}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{job.customer_name}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{job.product_description}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center',fontWeight:500}}>{job.quantity}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{job.delivery_date}</td>
                    <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                      <span style={{fontSize:9,fontWeight:700,padding:'2px 8px',borderRadius:20,
                        textTransform:'uppercase',background:sc.bg,color:sc.color}}>
                        {job.status.replace('_',' ')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Inventory Officer only sections */}
      {isInventoryOfficer && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
          {/* Low Stock */}
          {low_stock.length > 0 && (
            <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
              <div style={{background:'#8F2E24',padding:'12px 16px'}}>
                <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
                  📦 Low Stock ({low_stock.length})
                </span>
              </div>
              <div style={{padding:0}}>
                {low_stock.map((m,i) => (
                  <div key={m.name} style={{padding:'10px 16px',borderBottom:'1px solid #F2EFE7',
                    background:i%2===0?'#fff':'#FFF8F8'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500,color:'#3B2A21'}}>{m.name}</div>
                        <div style={{fontSize:11,color:'#8F2E24',marginTop:2}}>
                          Stock: <strong>{Number(m.current_stock).toLocaleString()} {m.unit_of_measure}</strong>
                          <span style={{color:'#8A8A8A',marginLeft:8}}>Reorder: {m.reorder_level}</span>
                        </div>
                      </div>
                      <div style={{width:40,height:40,borderRadius:'50%',background:'#FDEAEA',
                        display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>⚠</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Material Usage */}
          {recent_materials.length > 0 && (
            <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
              <div style={{background:'#556B2F',padding:'12px 16px'}}>
                <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
                  📊 Material Usage (Last 7 Days)
                </span>
              </div>
              <div style={{padding:16}}>
                {recent_materials.map((m,i) => (
                  <div key={i} style={{display:'flex',justifyContent:'space-between',
                    padding:'8px 0',borderBottom:'1px solid #F2EFE7',fontSize:12}}>
                    <span style={{color:'#3B2A21',fontWeight:500}}>{m.material}</span>
                    <span style={{color:'#556B2F',fontWeight:600}}>
                      {Number(m.total_used).toLocaleString()} {m.unit_of_measure}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Full Inventory Section - Inventory Officer only */}
      {isInventoryOfficer && (
        <>
          {/* Stock Movement Summary */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginTop:16,marginBottom:16}}>
            <div style={{background:'#E8F5EB',borderRadius:10,padding:'14px 16px',border:'1px solid #B8DEC0'}}>
              <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',color:'#2E6B3A',marginBottom:6,letterSpacing:'.1em'}}>Stock In This Month</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#2E6B3A'}}>{Number(stock_movement?.total_in||0).toLocaleString()}</div>
            </div>
            <div style={{background:'#FDEAEA',borderRadius:10,padding:'14px 16px',border:'1px solid #F5B8B8'}}>
              <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',color:'#8F2E24',marginBottom:6,letterSpacing:'.1em'}}>Stock Out This Month</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#8F2E24'}}>{Number(stock_movement?.total_out||0).toLocaleString()}</div>
            </div>
            <div style={{background:'#F2EFE7',borderRadius:10,padding:'14px 16px',border:'1px solid #DDD4C0'}}>
              <div style={{fontSize:9,fontWeight:600,textTransform:'uppercase',color:'#556B2F',marginBottom:6,letterSpacing:'.1em'}}>Total Materials</div>
              <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color:'#556B2F'}}>{all_materials?.length||0}</div>
            </div>
          </div>

          {/* All Materials Stock Levels */}
          <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',marginBottom:16}}>
            <div style={{background:'#556B2F',padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#A8893D',fontWeight:600}}>
                📦 Full Stock Levels
              </span>
              <span style={{fontSize:11,color:'rgba(242,239,231,.5)'}}>
                Total Value: ₦{Number((all_materials||[]).reduce((s,m)=>s+parseFloat(m.stock_value||0),0)).toLocaleString()}
              </span>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead><tr style={{background:'#F2EFE7'}}>
                {['Material','Category','Current Stock','Reorder Level','WAC','Stock Value','Status'].map(h=>(
                  <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(all_materials||[]).map((m,i) => {
                  const isLow = parseFloat(m.current_stock) <= parseFloat(m.reorder_level);
                  return (
                    <tr key={i} style={{background:isLow?'#FFF8F8':i%2===0?'#fff':'#F9F9F9'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500,color:isLow?'#8F2E24':'#3B2A21'}}>{m.name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{m.category||'—'}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:isLow?'#8F2E24':'#2E6B3A'}}>{Number(m.current_stock).toLocaleString()} {m.unit_of_measure}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{Number(m.reorder_level).toLocaleString()}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif'}}>₦{Number(m.weighted_avg_cost||0).toLocaleString()}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',color:'#556B2F'}}>₦{Number(m.stock_value||0).toLocaleString()}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {isLow ? (
                          <span style={{fontSize:9,fontWeight:700,background:'#FDEAEA',color:'#8F2E24',padding:'2px 8px',borderRadius:20}}>LOW STOCK</span>
                        ) : (
                          <span style={{fontSize:9,fontWeight:700,background:'#E8F5EB',color:'#2E6B3A',padding:'2px 8px',borderRadius:20}}>OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pending Purchase Orders */}
          {pending_pos?.length > 0 && (
            <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden',marginBottom:16}}>
              <div style={{background:'#1E4FAD',padding:'12px 16px'}}>
                <span style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1rem',color:'#F2EFE7',fontWeight:600}}>
                  📋 Pending Purchase Orders ({pending_pos.length})
                </span>
              </div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#F2EFE7'}}>
                  {['PO Number','Supplier','Order Date','Items'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pending_pos.map((po,i) => (
                    <tr key={po.po_number} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#1E4FAD'}}>{po.po_number}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{po.supplier_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A'}}>{po.order_date}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',textAlign:'center',fontWeight:600}}>{po.line_items}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
